import { RouterStore } from "@superwf/mobx-react-router";
import { makeAutoObservable } from "mobx";
import {
  Api,
  AuthPayload,
  LoginPayload,
  SignUpPayload,
  SocialRegisterPayload,
  User,
} from "services";

export type SocialLoginResult = "success" | "needs_register";

export class UserStore {
  private userApi: Api["userApi"];
  private routes: RouterStore;
  private generalStore: any;

  constructor(rootStore: any) {
    this.userApi = rootStore.generalStore.api.userApi;
    this.routes = rootStore.routingStore;
    this.generalStore = rootStore.generalStore;
    makeAutoObservable(this);
  }

  private authenticateAndRedirect = (authPayload: AuthPayload): void => {
    User.authenticate(authPayload);

    if (User.isStoreAdmin) {
      this.routes.push("/stores");
      return;
    }

    if (!authPayload.hasStore) {
      this.routes.push("/stores/add");
      return;
    }

    this.routes.push("/");
  };

  public login = async (payload: LoginPayload): Promise<void> => {
    const authPayload = await this.userApi.login(payload);
    if (!authPayload) return;
    this.authenticateAndRedirect(authPayload);
  };

  public socialLogin = async (
    provider: string,
    token: string,
    client?: string,
  ): Promise<SocialLoginResult> => {
    try {
      const authPayload = await this.userApi.socialLogin(
        { provider, token, client },
        { skipErrorHandler: true },
      );
      if (!authPayload) return "success";
      this.authenticateAndRedirect(authPayload);
      return "success";
    } catch (error) {
      try {
        const err = JSON.parse(error.message);
        if (err.status === 404 && err.tokenType === "USER_NOT_REGISTERED") {
          return "needs_register";
        }
      } catch {}
      this.generalStore.handleApiError(error);
      return "success";
    }
  };

  public socialRegister = async (
    payload: SocialRegisterPayload,
  ): Promise<{ message: string } | undefined> => {
    return await this.userApi.socialRegister(payload);
  };

  public getGoogleAuthUrl = async (
    client: string,
  ): Promise<{ url: string }> => {
    return await this.userApi.getGoogleAuthUrl(client);
  };

  public getMicrosoftAuthUrl = async (
    client: string,
  ): Promise<{ url: string }> => {
    return await this.userApi.getMicrosoftAuthUrl(client);
  };

  public getAppleAuthUrl = async (
    client: string,
  ): Promise<{ url: string }> => {
    return await this.userApi.getAppleAuthUrl(client);
  };

  public signUp = async (
    code: string,
    payload: SignUpPayload,
  ): Promise<void> => {
    const authPayload = await this.userApi.signUp(code, payload);
    if (!authPayload) return;
    this.authenticateAndRedirect(authPayload);
  };
}
